export const formatToReadableDate = (dateStr: string | Date): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const padTwoDigits = (value: number): string => String(value).padStart(2, "0");

function formatUTCDateTime(date: Date): string {
  return `${date.getUTCFullYear()}-${padTwoDigits(
    date.getUTCMonth() + 1
  )}-${padTwoDigits(date.getUTCDate())} ${padTwoDigits(
    date.getUTCHours()
  )}:${padTwoDigits(date.getUTCMinutes())}:${padTwoDigits(
    date.getUTCSeconds()
  )}+00`;
}

export function formatSlotDateTimeToUTCString(dateInput?: string | null): string {
  const now = new Date();
  const trimmed = typeof dateInput === "string" ? dateInput.trim() : "";
  const match = trimmed.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s]+(\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}))?)?$/
  );

  if (!match) return formatUTCDateTime(now);

  const [, rawYear, rawMonth, rawDay, rawHour, rawMinute, rawSecond] = match;
  const year = Number(rawYear);
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const hasTime = rawHour != null;
  const hour = hasTime ? Number(rawHour) : now.getUTCHours();
  const minute = hasTime ? Number(rawMinute ?? 0) : now.getUTCMinutes();
  const second = hasTime ? Number(rawSecond ?? 0) : now.getUTCSeconds();
  const millisecond = hasTime ? 0 : now.getUTCMilliseconds();

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return formatUTCDateTime(now);
  }

  const dateOnly = new Date(Date.UTC(year, month - 1, day));
  if (
    dateOnly.getUTCFullYear() !== year ||
    dateOnly.getUTCMonth() !== month - 1 ||
    dateOnly.getUTCDate() !== day
  ) {
    return formatUTCDateTime(now);
  }

  const utcTime = Date.UTC(
    year,
    month - 1,
    day,
    hour,
    minute,
    second,
    millisecond
  );

  return formatUTCDateTime(
    new Date(hasTime ? utcTime - IST_OFFSET_MS : utcTime)
  );
}

export const formatDeliveryDate = (deliveryDays: number) => {
  const today = new Date();

  const deliveryDate = new Date(today);
  deliveryDate.setDate(today.getDate() + Number(deliveryDays));

  const day = String(deliveryDate.getDate()).padStart(2, "0");
  const year = String(deliveryDate.getFullYear() % 100).padStart(2, "0");
  const month = deliveryDate.toLocaleString("en-US", { month: "long" });

  let formattedMonth = month;
  if (
    [
      "January",
      "February",
      "March",
      "April",
      "August",
      "September",
      "October",
      "November",
      "December",
    ].includes(month)
  ) {
    formattedMonth = `${month.slice(0, 3)}`;
  }

  return `${day} ${formattedMonth} '${year}`;
};

export const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (seconds < 60) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export const getDaysUntilDateString = (targetDateStr: string): number => {
  // Expects format like '20July 2025'
  if (!targetDateStr) return 0;
  const targetDate = new Date(targetDateStr);
  const today = new Date();
  targetDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();

  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

export const getStrapiFormattedDate = (timestamp: any, locale = "en-IN") => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const [day, month, year] = date
    .toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .split(" ");

  const timeString = date
    .toLocaleTimeString(locale, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
    .toLocaleUpperCase();

  const timeZone = "IST";

  return `${day} ${month}, ${year}, ${timeString} ${timeZone}`;
};

export const getDaysFromTomorrow = (targetDateStr: string): number => {
  if (!targetDateStr) return 0;
  const targetDate = new Date(targetDateStr);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate());

  targetDate.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - tomorrow.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

export function formatFullDate(rawDate: string) {
  const date = new Date(rawDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = String(date.getFullYear() % 100).padStart(2, "0");
  return `${day} ${month} '${year}`;
}

export const formatReportDate = (report_date: any) => {
  const year = report_date.year % 100;

  const shortMonths = ["Jan", "Feb", "Mar", "April", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
  const formattedMonth = shortMonths[report_date.month - 1];

  return `${formattedMonth} '${year}`;
};


export const formattedReportDateUtils = (
  report_date: { day: number; month: number; year: number } | null | undefined
): string | null => {
  if (!report_date) return null;

  const date = new Date(
    Date.UTC(report_date.year, report_date.month - 1, report_date.day)
  );
  const day = date.getUTCDate();
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  const ordinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return `${ordinal(day)} ${month} ${year}`;
};

export const getDeliveryDateFromDays = (days: number): string => {
  if (!days || isNaN(days)) return "";
  return formatDeliveryDate(days);
};

export const parseWaitingPeriodToDays = (waitingPeriod: string): number => {
  if (!waitingPeriod) return 0;
  const match = waitingPeriod.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};
